import { Box } from "@mui/system";
import {
  Button,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { closeIcon } from "assets/images";
import { Controller, useForm } from "react-hook-form";
import { IAddEditEducator } from "utils/interfaces/educator";
import { useEffect } from "react";
import { EDUCATOR_STATUS, EDUCATOR_TYPES } from "utils/constants/educator";
import { IRole } from "utils/interfaces/role";
import { EducatorStatus } from "utils/enums/educator";
import { CLOSE_TOOLTIP_TITLE, DEFAULT_PAGE_NO } from "utils/constants/constant";
import { ClearIcon } from "@mui/x-date-pickers";

interface IEducatorFilterProps {
  filterBox: boolean;
  setFilterBox: any;
  handleApplyClick: any;
  filteredData?: IAddEditEducator;
  pageNo: number;
  setPageNo: any;
  setFilteredData: any;
  getEducatorList: any;
  setAppliedFilter: any;
  roleList: Array<IRole>;
  getStatisticsData: (
    filterData?: IAddEditEducator,
    filtered?: boolean
  ) => Promise<void>;
}

const EducatorFilter: React.FC<IEducatorFilterProps> = ({
  filterBox,
  setFilterBox,
  handleApplyClick,
  filteredData,
  pageNo,
  setPageNo,
  setFilteredData,
  getEducatorList,
  setAppliedFilter,
  roleList,
  getStatisticsData,
}) => {
  const { handleSubmit, reset, setValue, watch, control } =
    useForm<IAddEditEducator>({
      defaultValues: {
        educatorType: 0,
        status: null,
        roleFilter: 0,
      },
    });

  useEffect(() => {
    if (filteredData) {
      setValue("educatorType", filteredData?.educatorType);
      setValue("status", filteredData?.status);
      setValue("roleFilter", filteredData?.roleFilter);
    } else {
      reset();
    }
  }, [filteredData, setValue, filterBox, reset]);

  const onClearClick = () => {
    reset();
    setFilterBox(false);
    setAppliedFilter(false);
    document.body.classList.toggle("filter-open");
    if (pageNo === DEFAULT_PAGE_NO) {
      getEducatorList();
      getStatisticsData();
    } else {
      setPageNo(1);
    }
    setFilteredData();
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleApplyClick)}>
        <Box className="filter-popover">
          <Box className="filter-header">
            <Typography variant="h4">Filter</Typography>
            <Tooltip
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              title={CLOSE_TOOLTIP_TITLE}
              arrow
            >
              <IconButton
                onClick={() => {
                  setFilterBox(false);
                  document.body.classList.toggle("filter-open");
                }}
              >
                <img src={closeIcon} alt="close" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box className="filter-body">
            <FormControl fullWidth className="select">
              <InputLabel id="user-type-label" shrink={!!watch("educatorType")}>
                User Type
              </InputLabel>
              <Controller
                name="educatorType"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="user-type-label"
                    id="user-type"
                    value={field.value ?? ""}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("educatorType", null);
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                  >
                    {EDUCATOR_TYPES.map((educator) => (
                      <MenuItem key={educator.value} value={educator.value}>
                        {educator.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormControl fullWidth className="select">
              <InputLabel
                id="status-select-label"
                shrink={
                  watch("status") === EducatorStatus.Inactive ||
                  watch("status") === EducatorStatus.Active
                }
              >
                Status
              </InputLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="status-select-label"
                    id="status-select"
                    value={field.value}
                    displayEmpty
                    endAdornment={
                      field.value || field.value === 0 ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("status", null);
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                  >
                    {EDUCATOR_STATUS?.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <FormControl fullWidth className="select">
              <InputLabel
                id="role-select-label"
                shrink={watch("roleFilter") !== 0}
              >
                Role
              </InputLabel>
              <Controller
                name="roleFilter"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="role-select-label"
                    id="role-select"
                    value={field.value}
                    displayEmpty
                    endAdornment={
                      field.value ? (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setValue("roleFilter", 0);
                          }}
                          edge="end"
                          className="clear-btn"
                        >
                          <ClearIcon />
                        </IconButton>
                      ) : null
                    }
                  >
                    {roleList?.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.roleName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>
          <Box className="filter-footer">
            <Button variant="contained" className="btn-apply" type="submit">
              Apply
            </Button>
            <Button
              variant="outlined"
              className="btn-clear"
              onClick={onClearClick}
              disabled={
                !(
                  watch("educatorType") ||
                  watch("status") === EducatorStatus.Inactive ||
                  watch("status") === EducatorStatus.Active ||
                  watch("roleFilter")
                )
              }
            >
              Clear
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default EducatorFilter;
